import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Button, Dropdown, Form } from 'react-bootstrap';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { determineTaskBucket } from './taskCardUtils';
import TasksFlatGridView from './views/TasksFlatGridView';
import TasksGroupedListView from './views/TasksGroupedListView';
import TasksKanbanView from './views/TasksKanbanView';
import useAppChoices from '../../hooks/useAppChoices';
import { ACCURATE_ROLES, ENDPOINTS, TASK_ROLES } from '../../helpers/constants';
import AlertModal from '../AlertModal';
import AddEditTaskModal from '../services/pipeline/Tasks/AddEditTaskModal';
import { TasksFlatGridLoadingSkeleton, TasksKanbanColumnsLoadingSkeleton } from './TaskSearchItemSkeleton';
import TaskFilterBuilder from './TaskFilterBuilder';
import {
  ArrowDownWideNarrow,
  Funnel,
  LayoutGrid,
  ListTodo,
  Plus,
  SquareKanban
} from 'lucide-react';

import useQuery from '../../hooks/useQueryParams';

const FLAT_PAGE_SIZE = 20;

const sortByOptionsClient = [
  { name: 'Newest first', value: 'createdAt' },
  { name: 'Due date', value: 'dueDate' }
];

const STATUS_TABS = [
  { key: 'Incomplete', label: 'Incomplete' },
  { key: 'Complete', label: 'Completed' },
  { key: 'All', label: 'All' },
  { key: 'Assigned to me', label: 'Assigned to me' },
  { key: 'Other', label: 'Other' }
];

const TASK_VIEWS = [
  {
    id: 'flat',
    Icon: LayoutGrid,
    label: 'Flat grid'
  },
  {
    id: 'grouped',
    Icon: ListTodo,
    label: 'Grouped'
  },
  {
    id: 'kanban',
    Icon: SquareKanban,
    label: 'Kanban'
  }
];

const getViewTabStyle = isActive => ({
  padding: '12px 20px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  border: 'none',
  background: isActive ? '#fff' : 'transparent',
  color: isActive ? '#185FA5' : '#888',
  borderBottom: `2px solid ${isActive ? '#185FA5' : 'transparent'}`,
  transition: 'all 0.15s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px'
});

const getFilterPillStyle = isActive => ({
  padding: '5px 14px',
  fontSize: '12px',
  borderRadius: '20px',
  border: `1px solid ${isActive ? '#85b7eb' : '#ddd'}`,
  background: isActive ? '#e6f1fb' : '#fff',
  color: isActive ? '#185fa5' : '#888',
  fontWeight: 500,
  lineHeight: 1.4
});

const SortToggle = React.forwardRef(({ onClick, disabled, children }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-link btn-sm text-muted p-0 border-0 d-inline-flex align-items-center shadow-none"
    style={{ fontSize: '11px' }}
  >
    {children}
  </button>
));

const SearchTasks = () => {
  const { taskId: taskIdFromUrl } = useQuery();
  const deepLinkHandled = useRef(null);
  const fetchRequestIdRef = useRef(0);
  const fetchAbortRef = useRef(null);
  const filterContainerRef = useRef(null);
  const user = useAppChoices('users');
  const savedView = useMemo(() => localStorage.getItem('tasks-active-view'), []);

  const processorsOptions = useMemo(
    () =>
      user
        .filter(
          u =>
            TASK_ROLES.includes(u.role) &&
            u.isEnabled
        )
        .map(p => ({ label: p.name, value: p._id })),
    [user]
  );

  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flatTasks, setFlatTasks] = useState([]);
  const [taskBuckets, setTaskBuckets] = useState({
    overdue: [],
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
    pastTasks: []
  });
  const [statusCounts, setStatusCounts] = useState({
    Incomplete: 0,
    Complete: 0,
    Other: 0,
    All: 0,
    'Assigned to me': 0
  });
  const [sortBy, setSortBy] = useState('createdAt');

  const [showFilterPopup, setShowFilterPopup] = useState(false);

  const [role] = useState(localStorage.getItem('user-role'));

  const [activeStatus, setActiveStatus] = useState('Assigned to me');
  const [activeView, setActiveView] = useState(() => {
    return savedView && ['flat', 'grouped', 'kanban'].includes(savedView) ? savedView : 'flat';
  });
  const [revisionMode, setRevisionMode] = useState(false);
  const [serviceTypeOptions] = useState(['Lien Search', 'Estoppel', 'Survey']);
  const [serviceTypeValues, setServiceTypeValues] = useState([]);
  const [taskModalMeta, setTaskModalMeta] = useState(null);
  const [taskDeleteModalMeta, setTaskDeleteModalMeta] = useState(null);
  const [processorsValues, setProcessorsValues] = useState([]);
  const [laterHasMore, setLaterHasMore] = useState(false);
  const [pastHasMore, setPastHasMore] = useState(false);
  const [laterLoadingMore, setLaterLoadingMore] = useState(false);
  const [pastLoadingMore, setPastLoadingMore] = useState(false);
  const [flatHasMore, setFlatHasMore] = useState(false);
  const [flatLoadingMore, setFlatLoadingMore] = useState(false);
  const [laterTotal, setLaterTotal] = useState(0);
  const [pastTotal, setPastTotal] = useState(0);
  const isAccurateUser = useMemo(() => ACCURATE_ROLES.includes(role), [role]);

  const areFiltersApplied = useMemo(() => {
    const serviceTypeFiltered = serviceTypeValues.length > 0;
    const processorFiltered = isAccurateUser && processorsValues.length > 0;
    return serviceTypeFiltered || processorFiltered;
  }, [serviceTypeValues, processorsValues, isAccurateUser]);

  const applyFilters = ({ serviceTypeValues: newServiceTypes, processorsValues: newProcessors }) => {
    setServiceTypeValues(newServiceTypes);
    setProcessorsValues(newProcessors);
    setShowFilterPopup(false);
  };

  const buildFetchRequestBody = (overrides = {}) => {
    return {
      viewMode: activeView,
      statusTab: activeStatus,
      sort:
        activeView === 'flat'
          ? { [sortBy]: sortBy === 'createdAt' ? -1 : 1 }
          : undefined,
      filter: {
        assignedProcessors:
          isAccurateUser && processorsValues.length > 0
            ? { $in: processorsValues }
            : undefined
      },
      isRevisionMode: revisionMode,
      orderTypes: serviceTypeValues,
      laterLimit: 5,
      page: 1,
      pageSize: FLAT_PAGE_SIZE,
      ...overrides
    };
  };

  const applyTasksResponse = (response, mode = 'full') => {
    if (!response) return;

    if (response.statusCounts) {
      setStatusCounts(response.statusCounts);
    }

    if (mode === 'flat') {
      const tasks = response.tasks || [];
      setFlatTasks(tasks);
      setFlatHasMore(tasks.length < (response.total ?? 0));
      return;
    }

    if (mode === 'appendFlat') {
      setFlatTasks(prev => {
        const merged = [...prev, ...(response.tasks || [])];
        setFlatHasMore(merged.length < (response.total ?? 0));
        return merged;
      });
      return;
    }

    if (mode === 'appendLater') {
      setTaskBuckets(prev => ({
        ...prev,
        later: [...prev.later, ...(response.later || [])]
      }));
      setLaterHasMore(Boolean(response.laterHasMore));
      return;
    }

    if (mode === 'appendPast') {
      setTaskBuckets(prev => ({
        ...prev,
        pastTasks: [...prev.pastTasks, ...(response.pastTasks || [])]
      }));
      setPastHasMore(Boolean(response.pastHasMore));
      return;
    }

    setTaskBuckets({
      overdue: response.overdue || [],
      today: response.today || [],
      tomorrow: response.tomorrow || [],
      thisWeek: response.thisWeek || [],
      later: response.later || [],
      pastTasks: response.pastTasks || []
    });
    setLaterHasMore(Boolean(response.laterHasMore));
    setPastHasMore(Boolean(response.pastHasMore));
    setLaterTotal(response.laterTotal || 0);
    setPastTotal(response.pastTotal || 0);
  };

  const fetchTasks = async () => {
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_TASKS,
      method: 'POST',
      requestBody: buildFetchRequestBody(
        activeView !== 'flat' ? { laterSkip: 0, pastSkip: 0 } : {}
      ),
      signal: controller.signal
    });

    if (requestId !== fetchRequestIdRef.current) return;

    if (error) {
      if (error !== ABORT_ERROR) {
        toast.error(error);
      }
      setLoading(false);
      return;
    }

    applyTasksResponse(response, activeView === 'flat' ? 'flat' : 'full');
    setLoading(false);
  };

  const loadMoreLater = async () => {
    const laterSkip = taskBuckets.later.length;
    setLaterLoadingMore(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_TASKS,
      method: 'POST',
      requestBody: buildFetchRequestBody({ loadMoreLater: true, laterSkip })
    });

    setLaterLoadingMore(false);

    if (error) {
      toast.error(error);
      return;
    }

    applyTasksResponse(response, 'appendLater');
  };

  const loadMorePast = async () => {
    const pastSkip = taskBuckets.pastTasks.length;
    setPastLoadingMore(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_TASKS,
      method: 'POST',
      requestBody: buildFetchRequestBody({ loadMorePast: true, pastSkip })
    });

    setPastLoadingMore(false);

    if (error) {
      toast.error(error);
      return;
    }

    applyTasksResponse(response, 'appendPast');
  };

  const loadMoreFlat = async () => {
    if (flatLoadingMore || !flatHasMore) return;

    const nextPage = Math.floor(flatTasks.length / FLAT_PAGE_SIZE) + 1;
    setFlatLoadingMore(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_TASKS,
      method: 'POST',
      requestBody: buildFetchRequestBody({ page: nextPage })
    });

    setFlatLoadingMore(false);

    if (error) {
      toast.error(error);
      return;
    }

    applyTasksResponse(response, 'appendFlat');
  };

  useEffect(() => {
    fetchTasks();
  }, [processorsValues, sortBy, serviceTypeValues, activeStatus, activeView, revisionMode]);

  useEffect(() => () => fetchAbortRef.current?.abort(), []);

  useEffect(() => {
    if (!showFilterPopup) return;

    const handlePointerDown = event => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
        setShowFilterPopup(false);
      }
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setShowFilterPopup(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showFilterPopup]);

  // Save active view to localStorage
  useEffect(() => {
    localStorage.setItem('tasks-active-view', activeView);
  }, [activeView]);

  useEffect(() => {
    deepLinkHandled.current = null;
  }, [taskIdFromUrl]);

  useEffect(() => {
    if (!taskIdFromUrl || loading) return;
    if (deepLinkHandled.current === taskIdFromUrl) return;

    const focusTaskFromUrl = async () => {
      // Clear any filters and switch to "Assigned to me" status when opening from notification
      if (activeStatus !== 'Assigned to me' || serviceTypeValues.length > 0 || processorsValues.length > 0) {
        if (activeStatus !== 'Assigned to me') {
          setActiveStatus('Assigned to me');
        }
        if (serviceTypeValues.length > 0) {
          setServiceTypeValues([]);
        }
        if (processorsValues.length > 0) {
          setProcessorsValues([]);
        }
        // Don't mark as handled yet - let it run again after status/filters change and data loads
        return;
      }

      // Check if task exists in current view's data
      const allCurrentTasks = activeView === 'flat' 
        ? flatTasks 
        : Object.values(taskBuckets).flat();
      
      let task = allCurrentTasks.find(t => t._id === taskIdFromUrl);

      if (!task) {
        // Task not found in current view - might be on another page or filtered out
        // Fetch the specific task and prepend it to the list
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.FETCH_TASKS,
          method: 'POST',
          requestBody: { filter: { _id: taskIdFromUrl } }
        });

        if (error) {
          toast.error(error);
          deepLinkHandled.current = taskIdFromUrl;
          return;
        }

        if (!response?.tasks || response.tasks.length === 0) {
          toast.error('Task not found');
          deepLinkHandled.current = taskIdFromUrl;
          return;
        }

        task = response.tasks[0];

        // Mark as handled before updating state to prevent re-triggering
        deepLinkHandled.current = taskIdFromUrl;

        // Prepend the task to the appropriate data structure
        if (activeView === 'flat') {
          setFlatTasks(prev => {
            // Check if already exists (avoid duplicates)
            if (prev.some(t => t._id === task._id)) return prev;
            return [task, ...prev];
          });
        } else {
          const bucket = determineTaskBucket(task);
          setTaskBuckets(prev => {
            const exists = prev[bucket]?.some(t => t._id === task._id);
            return {
              ...prev,
              [bucket]: exists 
                ? prev[bucket] 
                : [task, ...(prev[bucket] || [])]
            };
          });
        }

        // Wait a bit for the task to be rendered in the DOM, then highlight and scroll
        setTimeout(() => {
          setHighlightedTaskId(taskIdFromUrl);
          setTimeout(() => {
            const element = document.getElementById(`task-row-${taskIdFromUrl}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          setTimeout(() => setHighlightedTaskId(null), 3000);
        }, 100);
        return;
      }

      // Task found in current view - highlight and scroll to it
      deepLinkHandled.current = taskIdFromUrl;
      setHighlightedTaskId(taskIdFromUrl);
      setTimeout(() => {
        const element = document.getElementById(`task-row-${taskIdFromUrl}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
      setTimeout(() => setHighlightedTaskId(null), 3000);
    };

    focusTaskFromUrl();
  }, [taskIdFromUrl, loading, flatTasks, taskBuckets, activeStatus, activeView, serviceTypeValues, processorsValues]);

  const taskMatchesActiveTab = task => {
    switch (activeStatus) {
      case 'Complete':
        return task.status === 'Complete';
      case 'Incomplete':
        return task.status !== 'Complete';
      case 'All':
        return true;
      default:
        return true;
    }
  };

  const updateTaskInList = (list, updatedTask) => {
    const index = list.findIndex(t => t._id === updatedTask._id);
    if (index === -1) return list;
    if (!taskMatchesActiveTab(updatedTask)) {
      return list.filter(t => t._id !== updatedTask._id);
    }
    return list.map(t => (t._id === updatedTask._id ? updatedTask : t));
  };

  const updateTaskStatus = updatedTask => {
    if (!updatedTask?._id) {
      fetchTasks();
      return;
    }

    if (activeView === 'flat') {
      setFlatTasks(prev => updateTaskInList(prev, updatedTask));
      return;
    }

    setTaskBuckets(prev => {
      const next = {};
      Object.keys(prev).forEach(key => {
        next[key] = updateTaskInList(prev[key] || [], updatedTask);
      });
      return next;
    });
  };

  const onTaskAddEdit = () => {
    toast.success('Task saved successfully');
    fetchTasks();
  };

  const deleteTask = async () => {
    setTaskDeleteModalMeta({ ...taskDeleteModalMeta, loading: true });
    try {
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.TASKS_WITH_ID(taskDeleteModalMeta._id),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error);
        return;
      }
      toast.success(`Task deleted successfully`);
      await fetchTasks();
    } catch (e) {
      toast.error(e);
    } finally {
      setTaskDeleteModalMeta(null);
    }
  };

  const onHandleTaskSubmit = async data => {
    const toBeUpdatedTask = taskModalMeta;
    setTaskModalMeta({ ...taskModalMeta, loading: true });
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: toBeUpdatedTask?._id ? ENDPOINTS.TASKS_WITH_ID(toBeUpdatedTask._id) : ENDPOINTS.Create_TASKS,
        method: toBeUpdatedTask?._id ? 'PUT' : 'POST',
        requestBody: { ...data, order: toBeUpdatedTask?.order?._id || data?.order?._id }
      });

      if (error) {
        toast.error(error);
        return;
      }
      onTaskAddEdit(response);
    } catch (e) {
      toast.error(e);
    } finally {
      setTaskModalMeta(null);
    }
  };

  return (
    <>
      <AlertModal
        show={taskDeleteModalMeta !== null}
        onHide={() => setTaskDeleteModalMeta(null)}
        onDismissClick={() => setTaskDeleteModalMeta(null)}
        onContinueClick={deleteTask}
        showProgress={taskDeleteModalMeta?.loading}
        alertText={`Are you sure you want to delete this task ? This action cannot be undone!`}
        progressText="Deleting task..."
      />
      <AddEditTaskModal
        show={taskModalMeta !== null}
        onHide={() => setTaskModalMeta(null)}
        onSubmit={onHandleTaskSubmit}
        showProgress={taskModalMeta?.loading}
        edit={taskModalMeta}
        requireServiceSelection={taskModalMeta != null && !taskModalMeta._id}
      />
      <Container fluid className="h-100 my-3">
        <div className="mb-0">
          <div className="d-flex align-items-center justify-content-between">
            <div style={{ flexShrink: 0 }}>
              <h6 className="font-weight-bold xxxlargeFont">Tasks</h6>
              <p className="text-muted midFont mb-0">Manage your tasks</p>
            </div>
            <Button
              variant="success"
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0 py-2 shadow-none"
              style={{ gap: 6 }}
              disabled={loading}
              onClick={() => setTaskModalMeta({})}
            >
              <Plus size={16} aria-hidden className="flex-shrink-0" />
              <span>Add task</span>
            </Button>
          </div>

          <div
            className="d-flex align-items-stretch mt-3"
            style={{
              background: '#f9f9f9',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: '12px 12px 0 0'
            }}
          >
            {TASK_VIEWS.map(({ id, Icon, label }) => {
              const isActive = activeView === id;
              return (
                <button
                  key={id}
                  type="button"
                  className="flex-grow-1 flex-md-grow-0 shadow-none"
                  style={getViewTabStyle(isActive)}
                  onClick={() => {
                    if (activeView === id) return;
                    setActiveView(id);
                  }}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </button>
              );
            })}
            <div className="d-flex align-items-center ml-auto px-3">
              <Form.Check
                type="switch"
                id="revision-mode-toggle"
                label="Revision Mode"
                checked={revisionMode}
                disabled={loading}
                onChange={event => setRevisionMode(event.target.checked)}
                className="switch-info mb-0 smallFont text-muted"
              />
            </div>
          </div>

          <div className="d-flex align-items-center flex-wrap py-2 border-bottom" style={{ gap: '8px' }}>
            {STATUS_TABS.map(({ key, label }) => {
              const isActive = activeStatus === key;
              return (
                <button
                  key={key}
                  type="button"
                  className="btn btn-sm shadow-none"
                  style={getFilterPillStyle(isActive)}
                  onClick={() => {
                    if (activeStatus === key) return;
                    setActiveStatus(key);
                  }}
                >
                  {label} <span style={{ opacity: 0.6 }}>({statusCounts[key] ?? 0})</span>
                </button>
              );
            })}
            <div className="d-flex align-items-center ml-auto" style={{ gap: '8px' }}>
              {activeView === 'flat' && (
                <Dropdown>
                  <Dropdown.Toggle as={SortToggle} id="tasks-sort-dropdown">
                    <ArrowDownWideNarrow size={14} className="mr-1 flex-shrink-0" />
                    {sortByOptionsClient.find(a => a.value === sortBy)?.name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu alignRight className="small">
                    {sortByOptionsClient.map(option => {
                      const isActive = sortBy === option.value;
                      return (
                        <Dropdown.Item 
                          key={option.value} 
                          onClick={() => setSortBy(option.value)}
                          className={isActive ? 'bg-info text-white' : ''}
                        >
                          {option.name}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <div className="position-relative" ref={filterContainerRef}>
                <Button
                  variant="outline-light"
                  type="button"
                  className="d-flex align-items-center px-2 py-1 border hover-muted shadow-none"
                  onClick={() => setShowFilterPopup(!showFilterPopup)}
                >
                  <Funnel size={16} className="align-middle mr-2" color="black" style={{ flexShrink: 0 }} />
                  <span className="fw-medium text-dark" style={{ fontSize: '14px', color: 'black' }}>
                    Filters
                  </span>
                  {areFiltersApplied && (
                    <div
                      className="d-flex tinyFont font-weight-bold bg-info align-items-center justify-content-center text-white rounded-circle"
                      style={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 16,
                        height: 16,
                      }}
                    >
                      {(serviceTypeValues.length > 0 ? 1 : 0) + (processorsValues.length > 0 ? 1 : 0)}
                    </div>
                  )}
                </Button>
                {showFilterPopup && (
                  <TaskFilterBuilder
                    isOpen={showFilterPopup}
                    onClose={() => setShowFilterPopup(false)}
                    serviceTypeOptions={serviceTypeOptions}
                    serviceTypeValues={serviceTypeValues}
                    processorsOptions={processorsOptions}
                    processorsValues={processorsValues}
                    onApplyFilters={applyFilters}
                    isAccurateUser={isAccurateUser}
                  />
                )}
              </div>
            </div>
          </div>

        </div>
        <div className="pt-2">
          {loading && activeView === 'kanban' && <TasksKanbanColumnsLoadingSkeleton />}
          {loading && activeView !== 'kanban' && <TasksFlatGridLoadingSkeleton />}
          {!loading && activeView === 'flat' && (
            <TasksFlatGridView
              tasks={flatTasks}
              hasMore={flatHasMore}
              loadingMore={flatLoadingMore}
              onLoadMore={loadMoreFlat}
              onTaskDelete={task => setTaskDeleteModalMeta(task)}
              onEditClick={task => setTaskModalMeta(task)}
              onStatusUpdate={updateTaskStatus}
              highlightedTaskId={highlightedTaskId}
              showDescription={revisionMode}
            />
          )}
          {!loading && activeView === 'grouped' && (
            <TasksGroupedListView
              taskBuckets={taskBuckets}
              laterHasMore={laterHasMore}
              pastHasMore={pastHasMore}
              laterLoadingMore={laterLoadingMore}
              pastLoadingMore={pastLoadingMore}
              onLoadMoreLater={loadMoreLater}
              onLoadMorePast={loadMorePast}
              onTaskDelete={task => setTaskDeleteModalMeta(task)}
              onEditClick={task => setTaskModalMeta(task)}
              onStatusUpdate={updateTaskStatus}
              statusTab={activeStatus}
              highlightedTaskId={highlightedTaskId}
              laterTotal={laterTotal}
              pastTotal={pastTotal}
              showDescription={revisionMode}
            />
          )}
          {!loading && activeView === 'kanban' && (
            <TasksKanbanView
              taskBuckets={taskBuckets}
              laterHasMore={laterHasMore}
              pastHasMore={pastHasMore}
              laterLoadingMore={laterLoadingMore}
              pastLoadingMore={pastLoadingMore}
              onLoadMoreLater={loadMoreLater}
              onLoadMorePast={loadMorePast}
              onTaskDelete={task => setTaskDeleteModalMeta(task)}
              onEditClick={task => setTaskModalMeta(task)}
              onStatusUpdate={updateTaskStatus}
              statusTab={activeStatus}
              highlightedTaskId={highlightedTaskId}
              laterTotal={laterTotal}
              pastTotal={pastTotal}
              showDescription={revisionMode}
            />
          )}
        </div>
      </Container>
    </>
  );
};

export default SearchTasks;
